import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { FormikContextType, useFormikContext } from 'formik';
import { Link, navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import Heading, { HeadingVariant, HeadingScope } from 'src/components/Heading';
import { baseButtonStyles } from 'src/components/Button/Button';
import { mediaQuery } from 'src/theme/breakpoints';
import { colors } from 'src/theme/colors';
import { clearFormDataFromStorage } from 'src/components/MultistepForm/shared/helpers';
import { links } from 'src/components/MultistepForm/shared/constants';

const NavigationWrapper = styled.header`
  align-items: center;
  background-color: transparent;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px 20px;
  width: 100%;

  ${mediaQuery('sm')} {
    flex-direction: row;
    height: 80px;
    justify-content: space-between;
    margin: 0 auto;
    max-width: 1080px;
    padding: 20px 40px;
  } ;
`;

const Subtitle = styled.span`
  color: ${colors.blue500};
  font-size: 12px;
  ${mediaQuery('md')} {
    font-size: 14px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 40px;
  padding: 0 20px;
  text-align: center;
`;

const ButtonsWrapper = styled.div`
  align-items: center;
  display: flex;

  ${mediaQuery('sm', 'max')} {
    margin-top: 20px;
  }
`;

const ButtonBack = styled.button`
  ${baseButtonStyles}
  background-color: ${colors.blue300};
  margin-right: 20px;
`;

const Button = styled.button<Pick<FormNavigationProps, 'disabled'>>`
  ${baseButtonStyles}
  background-color: ${({ disabled }) =>
    disabled ? `${colors.blue100}` : `${colors.orange500}`};

  ${({ disabled }) =>
    disabled &&
    css`
      box-shadow: none;
      &:hover {
        background-color: ${colors.blue100};
        box-shadow: none;
        cursor: initial;
        transform: none;
      }
    `}
`;

interface FormNavigationProps {
  title: string;
  onBackClick?: () => void;
  backActive: boolean;
  isCompleted?: boolean;
  isLastStep?: boolean;
  disabled?: boolean;
}

const FormNavigation: React.FC<FormNavigationProps> = ({
  title,
  backActive,
  isCompleted = false,
  isLastStep,
  onBackClick,
}) => {
  const context: FormikContextType<any> = useFormikContext();

  useEffect(() => {
    setTimeout(() => context?.validateForm && context.validateForm());
  }, [title]);

  const reinitForm = (): void => {
    clearFormDataFromStorage();
    navigate(links.TRIP_FORM);
  };

  return (
    <>
      <NavigationWrapper>
        <Link to="/">
          <StaticImage
            src="../../../assets/images/logo-juodas.png"
            alt="Piligrimo kelionės"
            width={115}
            height={50}
          />
        </Link>
        <ButtonsWrapper>
          {backActive && (
            <ButtonBack type="button" onClick={onBackClick}>
              Atgal
            </ButtonBack>
          )}
          {!isCompleted && (
            <Button type="submit" disabled={!context?.isValid}>
              {isLastStep ? 'Kurti kelionę' : 'Sekantis'}
            </Button>
          )}

          {isCompleted && (
            <Button type="button" onClick={reinitForm}>
              Pildyti iš naujo
            </Button>
          )}
        </ButtonsWrapper>
      </NavigationWrapper>
      <TitleWrapper>
        <Subtitle>
          {isCompleted
            ? 'Atsižvelgus į tavo atsakymus sudarėme sąrašą kelionių, kurios tau tinka labiausiai'
            : 'Susikurk savo svajonių maršrutą'}
        </Subtitle>
        <HeadingScope>
          <Heading variant={HeadingVariant.FOXTROT}>{title}</Heading>
        </HeadingScope>
      </TitleWrapper>
    </>
  );
};

export default FormNavigation;
